import React from 'react';
import { array, bool, func, number, object, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, intlShape, injectIntl } from '../../../util/reactIntl';
import { timestampToDate } from '../../../util/dates';
import { propTypes } from '../../../util/types';
import { BOOKING_PROCESS_NAME } from '../../../transactions/transaction';
import * as validators from '../../../util/validators';

import isEmpty from 'lodash/isEmpty';

import {
  Form,
  H6,
  PrimaryButton,
  FieldLocationAutocompleteInput,
  FieldTextInput,
  FieldCheckboxGroupWithQuantity,
  FieldQuantityInput,
  IconStripe,
} from '../../../components';

import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';
import FieldDateAndTimeInput from './FieldDateAndTimeInput';

import css from './BookingTimeForm.module.css';
import arrayMutators from 'final-form-arrays';
import { getAdditionalFeeOptions } from '../helper';
import debounce from 'lodash/debounce';

const DEBOUNCE_WAIT_TIME = 600;

const identity = v => v;

// When the values of the form are updated we need to fetch
// lineItems from this template's backend for the EstimatedTransactionMaybe
// In case you add more fields to the form, make sure you add
// the values here to the orderData object.
const handleFetchLineItems = props => formValues => {
  const { listingId, isOwnListing, fetchLineItemsInProgress, onFetchTransactionLineItems } = props;
  const { bookingStartTime, bookingEndTime, selectedService } = formValues.values;
  const startDate = bookingStartTime ? timestampToDate(bookingStartTime) : null;
  const endDate = bookingEndTime ? timestampToDate(bookingEndTime) : null;
  const quantityRaw = formValues?.values?.quantity || 1;
  const quantity = Number.parseInt(quantityRaw, 10);
  const filteredService = selectedService?.filter(s => !isEmpty(s.option) && true);

  // Note: we expect values bookingStartTime and bookingEndTime to be strings
  // which is the default case when the value has been selected through the form
  const isStartBeforeEnd = bookingStartTime < bookingEndTime;

  if (
    bookingStartTime &&
    bookingEndTime &&
    isStartBeforeEnd &&
    !fetchLineItemsInProgress &&
    !quantityRaw
  ) {
    onFetchTransactionLineItems({
      orderData: {
        bookingStart: startDate,
        bookingEnd: endDate,
        selectedService: filteredService,
        quantity,
      },
      listingId,
      isOwnListing,
    });
  }
};

const handleChangeService = (formValues, props, e) => {
  const selectedService = formValues?.values?.selectedService || [];
  const filteredService = selectedService?.filter(s => !isEmpty(s.option) && true);
  const quantityRaw = formValues?.values?.quantity || 1;
  const quantity = Number.parseInt(quantityRaw, 10);

  const { listingId, isOwnListing, fetchLineItemsInProgress, onFetchTransactionLineItems } = props;
  const { bookingStartTime, bookingEndTime } = formValues?.values;
  const startDate = bookingStartTime ? timestampToDate(bookingStartTime) : null;
  const endDate = bookingEndTime ? timestampToDate(bookingEndTime) : null;

  // Note: we expect values bookingStartTime and bookingEndTime to be strings
  // which is the default case when the value has been selected through the form
  const isStartBeforeEnd = bookingStartTime < bookingEndTime;

  if (bookingStartTime && bookingEndTime && isStartBeforeEnd && !fetchLineItemsInProgress) {
    onFetchTransactionLineItems({
      orderData: {
        bookingStart: startDate,
        bookingEnd: endDate,
        selectedService: filteredService,
        quantity,
      },
      listingId,
      isOwnListing,
    });
  }
};

const debouncedHandleChangeService = debounce(handleChangeService, DEBOUNCE_WAIT_TIME, {
  leading: false,
  trailing: true,
});

export const BookingTimeFormComponent = props => {
  const {
    rootClassName,
    className,
    price: unitPrice,
    dayCountAvailableForBooking,
    marketplaceName,
    onFetchTransactionLineItems,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  return (
    <FinalForm
      {...rest}
      unitPrice={unitPrice}
      mutators={{ ...arrayMutators }}
      render={formRenderProps => {
        const {
          endDatePlaceholder,
          startDatePlaceholder,
          form,
          pristine,
          handleSubmit,
          intl,
          isOwnListing,
          listingId,
          values,
          monthlyTimeSlots,
          onFetchTimeSlots,
          timeZone,
          lineItems,
          fetchLineItemsInProgress,
          fetchLineItemsError,
          payoutDetailsWarning,
          listing,
          marketplaceCurrency,
        } = formRenderProps;

        const startTime = values && values.bookingStartTime ? values.bookingStartTime : null;
        const endTime = values && values.bookingEndTime ? values.bookingEndTime : null;
        const startDate = startTime ? timestampToDate(startTime) : null;
        const endDate = endTime ? timestampToDate(endTime) : null;

        // This is the place to collect breakdown estimation data. See the
        // EstimatedCustomerBreakdownMaybe component to change the calculations
        // for customized payment processes.
        const breakdownData =
          startDate && endDate
            ? {
                startDate,
                endDate,
              }
            : null;

        const showEstimatedBreakdown =
          breakdownData && lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;

        const services = listing?.attributes?.publicData?.servicesPricing;
        const selectedService = values?.selectedService;
        const selectedServiceLabel = services?.find(s => s.id === selectedService)?.label;
        const servicesParams = { intl, valuesArray: services, currency: marketplaceCurrency };
        const servicesFee = services && getAdditionalFeeOptions(servicesParams);
        const quantityLabel = <FormattedMessage id="BookingTimeForm.quantityLabel" />;

        return (
          <Form onSubmit={handleSubmit} className={classes} enforcePagePreloadFor="CheckoutPage">
            <FormSpy
              subscription={{ values: true }}
              onChange={values => {
                debouncedHandleChangeService(values, props);
              }}
            />
            {!!services?.length && (
              <div className={css.bookingOptionsItem}>
                <FieldCheckboxGroupWithQuantity
                  id={`selectedService`}
                  name="selectedService"
                  options={servicesFee}
                  arrayName={'selectedService'}
                  optionLabelClassName={css.optionText}
                  className={css.bookingCheckboxGroup}
                  quantityClassName={css.quantityInput}
                  intl={intl}
                />
              </div>
            )}

            {!!values?.selectedService && (
              <div className={css.quantityHolder}>
                <label>{quantityLabel}</label>
                <FieldQuantityInput
                  id={`quantity`}
                  name={`quantity`}
                  type="number"
                  min={1}
                  max={10}
                  className={css.quantity}
                  initialValue={values.quantity || 1}
                />
              </div>
            )}

            {!!values?.selectedService && monthlyTimeSlots && timeZone ? (
              <>
                <FieldDateAndTimeInput
                  startDateInputProps={{
                    label: intl.formatMessage({ id: 'BookingTimeForm.bookingStartTitle' }),
                    placeholderText: startDatePlaceholder,
                  }}
                  endDateInputProps={{
                    label: intl.formatMessage({ id: 'BookingTimeForm.bookingEndTitle' }),
                    placeholderText: endDatePlaceholder,
                  }}
                  className={css.bookingDates}
                  listingId={listingId}
                  onFetchTimeSlots={onFetchTimeSlots}
                  monthlyTimeSlots={monthlyTimeSlots}
                  values={values}
                  intl={intl}
                  form={form}
                  pristine={pristine}
                  timeZone={timeZone}
                  dayCountAvailableForBooking={dayCountAvailableForBooking}
                  handleFetchLineItems={handleFetchLineItems(props)}
                  selectedService={selectedService}
                />
              </>
            ) : null}

            <FieldLocationAutocompleteInput
              rootClassName={css.locationAddress}
              inputClassName={css.locationAutocompleteInput}
              iconClassName={css.locationAutocompleteInputIcon}
              predictionsClassName={css.predictionsRoot}
              validClassName={css.validLocation}
              name="location"
              label={intl.formatMessage({ id: 'BookingTimeForm.address' })}
              placeholder={intl.formatMessage({
                id: 'BookingTimeForm.addressPlaceholder',
              })}
              useDefaultPredictions={false}
              format={identity}
              valueFromForm={values.location}
              validate={validators.composeValidators(
                validators.autocompleteSearchRequired(
                  intl.formatMessage({
                    id: 'BookingTimeForm.addressRequired',
                  })
                ),
                validators.autocompletePlaceSelected(
                  intl.formatMessage({
                    id: 'BookingTimeForm.addressNotRecognized',
                  })
                )
              )}
            />

            <FieldTextInput
              className={css.sport}
              type="text"
              id="sport"
              name="sport"
              label={intl.formatMessage({
                id: 'BookingTimeForm.sportLabel',
              })}
              placeholder={intl.formatMessage({
                id: 'BookingTimeForm.sportPlaceholder',
              })}
              validate={validators.required(
                intl.formatMessage({
                  id: 'BookingTimeForm.sportRequired',
                })
              )}
            />

            <FieldTextInput
              className={css.specificRequirements}
              type="textarea"
              id="specificRequirements"
              name="specificRequirements"
              label={intl.formatMessage({
                id: 'BookingTimeForm.specificRequirementsLabel',
              })}
              placeholder={intl.formatMessage({
                id: 'BookingTimeForm.specificRequirementsPlaceholder',
              })}
            />

            {showEstimatedBreakdown ? (
              <div className={css.priceBreakdownContainer}>
                <H6 as="h3" className={css.bookingBreakdownTitle}>
                  <FormattedMessage id="BookingTimeForm.priceBreakdownTitle" />
                </H6>
                <hr className={css.totalDivider} />
                <EstimatedCustomerBreakdownMaybe
                  breakdownData={breakdownData}
                  lineItems={lineItems}
                  timeZone={timeZone}
                  currency={unitPrice.currency}
                  marketplaceName={marketplaceName}
                  processName={BOOKING_PROCESS_NAME}
                  service={selectedServiceLabel}
                  location={values?.location?.selectedPlace?.address}
                  sport={values?.sport}
                  specificRequirements={values?.specificRequirements}
                />
              </div>
            ) : null}

            {fetchLineItemsError ? (
              <span className={css.sideBarError}>
                <FormattedMessage id="BookingTimeForm.fetchLineItemsError" />
              </span>
            ) : null}

            <div className={css.submitButton}>
              <PrimaryButton type="submit" inProgress={fetchLineItemsInProgress}>
                <FormattedMessage id="BookingTimeForm.requestToBook" />
              </PrimaryButton>
            </div>

            <p className={css.finePrint}>
              {payoutDetailsWarning ? (
                payoutDetailsWarning
              ) : (
                <FormattedMessage
                  id={
                    isOwnListing
                      ? 'BookingTimeForm.ownListing'
                      : 'BookingTimeForm.youWontBeChargedInfo'
                  }
                />
              )}
            </p>

            <div className={css.iconStripe}>
              <IconStripe />
            </div>
          </Form>
        );
      }}
    />
  );
};

BookingTimeFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  isOwnListing: false,
  listingId: null,
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
};

BookingTimeFormComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  price: propTypes.money,
  isOwnListing: bool,
  listingId: propTypes.uuid,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,
  timeZone: string.isRequired,

  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,

  // for tests
  startDatePlaceholder: string,
  endDatePlaceholder: string,

  dayCountAvailableForBooking: number.isRequired,
};

const BookingTimeForm = compose(injectIntl)(BookingTimeFormComponent);
BookingTimeForm.displayName = 'BookingTimeForm';

export default BookingTimeForm;
